import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, LANGUAGE_FLAGS, SupportedLanguage, isValidLanguage } from '../../../types/i18n.ts'
import './index.css'

const LanguageSwitcher: React.FC = () => {
	const { i18n } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const [isOpen, setIsOpen] = useState(false)
	const dropdownRef = useRef<HTMLDivElement>(null)

	const currentLang = i18n.language as SupportedLanguage

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const changeLanguage = (newLang: SupportedLanguage) => {
		// Update i18n
		i18n.changeLanguage(newLang)
		localStorage.setItem('@language', newLang)

		// Update URL
		const pathParts = location.pathname.split('/').filter(Boolean)

		// Check if first part is a valid language code
		if (pathParts.length > 0 && isValidLanguage(pathParts[0])) {
			// Replace existing language prefix
			pathParts[0] = newLang
		} else {
			// Add language prefix
			pathParts.unshift(newLang)
		}

		// For default language (hr), don't add prefix
		if (newLang === 'hr') {
			if (isValidLanguage(pathParts[0])) {
				pathParts.shift()
			}
		}

		const newPath = '/' + pathParts.join('/')
		navigate(newPath + location.search)
		setIsOpen(false)
	}

	return (
		<div className='language-switcher' ref={dropdownRef}>
			<button
				className='language-switcher-btn'
				onClick={() => setIsOpen(!isOpen)}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
			>
				<span className='language-flag'>{LANGUAGE_FLAGS[currentLang]}</span>
				<span className='language-code'>{currentLang.toUpperCase()}</span>
				<span className={`language-arrow ${isOpen ? 'open' : ''}`}>▼</span>
			</button>

			{isOpen && (
				<ul className='language-dropdown' role="listbox">
					{SUPPORTED_LANGUAGES.map(lang => (
						<li
							key={lang}
							className={`language-option ${lang === currentLang ? 'active' : ''}`}
							onClick={() => changeLanguage(lang)}
							role="option"
							aria-selected={lang === currentLang}
						>
							<span className='language-flag'>{LANGUAGE_FLAGS[lang]}</span>
							<span className='language-name'>{LANGUAGE_NAMES[lang]}</span>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

export default LanguageSwitcher
